import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';

import { getLayout } from 'components/layouts/Sidebar';
import { useNotification } from 'hooks/useNotification';
import { useCommon } from 'hooks/useCommon';
import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';
import { withAuth } from 'lib/hocs/withAuth';
import { CompanyAttributes } from 'lib/models/company';
import { SocietySponsorshipProvider } from 'lib/providers/SocietySponsorshipProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { Sponsorships } from './Sponsorships/Sponsorships.component';
import { ExploreCompanies } from './ExploreCompanies/ExploreCompanies.component';
import { MessageCompanyModal } from './MessageCompanyModal/MessageCompanyModal.component';
import { Plans } from './Plans/Plans.component';
import { AddSponsorModal } from './AddSponsorModal/AddSponsorModal.component';
import { InviteSponsorModal } from './InviteSponsorModal/InviteSponsorModal.component';
import {
  SelectPlanModal,
  SponsorUpdatePlanDataType,
} from './Plans/SelectPlanModal/SelectPlanModal.component';
import { RemoveSponsorModal } from './RemoveSponsorModal/RemoveSponsorModal.component';

const SponsorshipsWrapper = () => {
  const { societyProfile } = useSocietyProfile();
  const notificationInstance = useNotification();
  const { user } = useAuth();
  const { onShare } = useCommon();

  const [showExploreCompanies, setShowExploreCompanies] =
    useState<boolean>(false);
  const [showSponsorshipPlans, setShowSponsorshipPlans] =
    useState<boolean>(false);
  const [plansPageUrl, setPlansPageUrl] = useState<string>('');
  const [messageCompanyData, setMessageCompanyData] = useState<
    CompanyAttributes | undefined
  >(undefined);
  const [addSponsorData, setAddSponsorData] = useState<
    CompanyAttributes | undefined
  >(undefined);
  const [inviteSponsorData, setInviteSponsorData] = useState<
    CompanyAttributes | undefined
  >(undefined);
  const [showSelectPlan, setShowSelectPlan] = useState<boolean>(false);
  const [selectPlanData, setSelectPlanData] = useState<
    SponsorUpdatePlanDataType | undefined
  >(undefined);
  const [isConversationSponsor, setIsConversationSponsor] =
    useState<boolean>(false);
  const [removeSponsorId, setRemoveSponsorId] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (societyProfile) {
      const url = `${window.location.origin}/student-societies/${societyProfile.slug}`;
      setPlansPageUrl(url);
    }
  }, [societyProfile]);

  const toggleSponsorshipPlansView = () => {
    setShowSponsorshipPlans((isVisible) => !isVisible);
  };

  const toggleExploreCompaniesView = () => {
    setShowExploreCompanies((isVisible) => !isVisible);
  };

  const toggleMessageModal = (company?: CompanyAttributes) => {
    setMessageCompanyData(company);
  };

  const toggleAddSponsorModal = (company?: CompanyAttributes) => {
    setAddSponsorData(company);
  };

  const toggleInviteSponsorModal = (company?: CompanyAttributes) => {
    setInviteSponsorData(company);
  };

  const toggleSelectPlanModal = (data?: SponsorUpdatePlanDataType) => {
    setShowSelectPlan((isVisible) => !isVisible);
    setSelectPlanData(data);
  };

  const toggleRemoveSponsorModal = (
    companyId?: string,
    isInConversation?: boolean
  ) => {
    setRemoveSponsorId(companyId);
    setIsConversationSponsor(!!isInConversation);
  };

  const goToPlansPage = () => {
    setShowExploreCompanies(false);
    toggleSponsorshipPlansView();
    toggleSelectPlanModal();
  };

  const copyOffersLink = useCallback(() => {
    navigator.clipboard.writeText(plansPageUrl);
    notificationInstance.success({
      title: 'Link copied',
      message: 'Society offers link copied successfully.',
    });
  }, [plansPageUrl]);

  const copyInvitationLink = () => {
    if (!user) return;
    const url = `${window.location.origin}${PAGE_ROUTES.RECRUITER_SIGN_UP}`;
    const referralLink = `${url}${
      user.referral_code ? `?referrer=${user.referral_code}` : ''
    }`;
    onShare('', referralLink, {
      title: 'Link copied',
      message: 'Invitation link copied to clipboard',
    });
  };

  const renderContent = useMemo(() => {
    if (showExploreCompanies) {
      return (
        <ExploreCompanies
          goBack={toggleExploreCompaniesView}
          onMessageCompany={toggleMessageModal}
          onAddSponsor={toggleAddSponsorModal}
          onInviteSponsor={toggleInviteSponsorModal}
          copyInvitationLink={copyInvitationLink}
          onSelectPlan={toggleSelectPlanModal}
        />
      );
    } else if (showSponsorshipPlans) {
      return (
        <Plans
          plansPageUrl={plansPageUrl}
          goBack={toggleSponsorshipPlansView}
          copyOffersLink={copyOffersLink}
        />
      );
    } else {
      return (
        <Sponsorships
          viewAllCompanies={toggleExploreCompaniesView}
          viewPlans={toggleSponsorshipPlansView}
          copyOffersLink={copyOffersLink}
          copyInvitationLink={copyInvitationLink}
          onMessageCompany={toggleMessageModal}
          onAddSponsor={toggleAddSponsorModal}
          onInviteSponsor={toggleInviteSponsorModal}
          onSelectPlan={toggleSelectPlanModal}
          onRemoveSponsor={toggleRemoveSponsorModal}
        />
      );
    }
  }, [showExploreCompanies, showSponsorshipPlans, plansPageUrl]);

  return (
    <>
      <Head>
        <title>Sponsorships | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <SocietySponsorshipProvider>
        {renderContent}
        <MessageCompanyModal
          open={!!messageCompanyData}
          onClose={toggleMessageModal}
          companyData={messageCompanyData}
        />
        <AddSponsorModal
          open={!!addSponsorData}
          onClose={toggleAddSponsorModal}
          companyData={addSponsorData}
        />
        <InviteSponsorModal
          open={!!inviteSponsorData}
          onClose={toggleInviteSponsorModal}
          companyData={inviteSponsorData}
          copyInvitationLink={copyInvitationLink}
        />
        <SelectPlanModal
          open={showSelectPlan}
          sponsorData={selectPlanData}
          onClose={toggleSelectPlanModal}
          goToPlans={goToPlansPage}
        />
        <RemoveSponsorModal
          open={!!removeSponsorId}
          onClose={toggleRemoveSponsorModal}
          companyId={removeSponsorId || ''}
          isInConversation={isConversationSponsor}
        />
      </SocietySponsorshipProvider>
    </>
  );
};

const SponsorshipsAuthWrapper = withAuth(SponsorshipsWrapper);

// @ts-ignore
SponsorshipsAuthWrapper.getLayout = getLayout;

export default SponsorshipsAuthWrapper;
